import React, { useState, useEffect, useRef, useCallback } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import styled from "styled-components";
import fetchClient from "../../api/fetchClient";
import message from "../../helper/message";

const SERVER_BATCH = 200; // busca até 200 por vez do servidor

const COLUMNS = [
  { key: "code",        label: "Código",       field: "code"        },
  { key: "name",        label: "Nome do Grupo", field: "name"        },
  { key: "integrarIFS", label: "Integrar IFS",  field: "integrarIFS" },
];

function TabGruposItens({ aplicacaoId }) {
  const [allItems, setAllItems]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [updating, setUpdating]   = useState({});
  const [search, setSearch]       = useState("");
  const [integrarFilter, setIntegrarFilter] = useState("");
  const [sort, setSort]           = useState({ col: "name", dir: "asc" });
  const [pageSize, setPageSize]   = useState(5);
  const [page, setPage]           = useState(0);
  const debounceRef               = useRef(null);

  // ── Busca TODOS os registros do servidor (segue nextLinks) ──────────────
  const fetchAll = useCallback(async () => {
    if (!aplicacaoId) return;
    setLoading(true);
    const collected = [];
    try {
      let nextLink = null;
      do {
        let url;
        if (nextLink) {
          url = `/ifs/item-groups?${new URLSearchParams({ aplicacaoId, $nextLink: nextLink })}`;
        } else {
          url = `/ifs/item-groups?${new URLSearchParams({ aplicacaoId, $top: SERVER_BATCH })}`;
        }
        const { data } = await fetchClient.get(url);
        collected.push(...(data.items || []));
        nextLink = data.odata?.nextLink ?? null;
      } while (nextLink);

      setAllItems(collected);
    } catch {
      message.error("Erro ao carregar grupos de itens");
    } finally {
      setLoading(false);
    }
  }, [aplicacaoId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filtro + ordenação client-side ──────────────────────────────────────
  const filteredItems = React.useMemo(() => {
    let result = allItems;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        String(i.code).toLowerCase().includes(q) ||
        (i.name || "").toLowerCase().includes(q)
      );
    }

    if (integrarFilter) {
      result = result.filter(i => i.integrarIFS === integrarFilter);
    }

    result = [...result].sort((a, b) => {
      const va = a[sort.col] ?? "";
      const vb = b[sort.col] ?? "";
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [allItems, search, integrarFilter, sort]);

  // ── Paginação client-side ───────────────────────────────────────────────
  const totalPages  = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePage    = Math.min(page, totalPages - 1);
  const pageItems   = filteredItems.slice(safePage * pageSize, safePage * pageSize + pageSize);

  // reset page when filters/pageSize change
  useEffect(() => { setPage(0); }, [search, integrarFilter, sort, pageSize]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(q), 200);
  };

  const handleSort = (col) => {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" }
    );
  };

  const toggleIntegra = async (code, current) => {
    const newVal = current === "Y" ? "N" : "Y";
    setUpdating(u => ({ ...u, [code]: true }));
    try {
      await fetchClient.patch(`/ifs/item-groups/${code}?aplicacaoId=${aplicacaoId}`, { U_SD_IntegraIFS: newVal });
      setAllItems(prev => prev.map(i => i.code === code ? { ...i, integrarIFS: newVal } : i));
    } catch {
      message.error("Erro ao atualizar grupo");
    } finally {
      setUpdating(u => ({ ...u, [code]: false }));
    }
  };

  const sortArrow = (col) => {
    if (sort.col !== col) return <SortIcon>↕</SortIcon>;
    return <SortIcon active>{sort.dir === "asc" ? "↑" : "↓"}</SortIcon>;
  };

  if (!aplicacaoId) {
    return <NoToken>ID da aplicação não disponível. Salve a aplicação antes de acessar esta aba.</NoToken>;
  }

  return (
    <Wrapper>
      <Header>
        <Title>
          Grupos de Itens SAP
          {!loading && <Count> ({filteredItems.length}{filteredItems.length !== allItems.length ? ` de ${allItems.length}` : ""})</Count>}
        </Title>
        <RefreshBtn type="button" onClick={fetchAll} disabled={loading}>
          {loading ? <CircularProgress size={14} /> : "↻ Atualizar"}
        </RefreshBtn>
      </Header>

      <SearchRow>
        <SearchInput
          type="text"
          placeholder="Filtrar por código ou nome..."
          value={search}
          onChange={handleSearch}
        />
        <IntegrarSelect
          value={integrarFilter}
          onChange={e => setIntegrarFilter(e.target.value)}
        >
          <option value="">Integrar IFS: Todos</option>
          <option value="Y">Integrar IFS: Sim (Y)</option>
          <option value="N">Integrar IFS: Não (N)</option>
        </IntegrarSelect>
      </SearchRow>

      {loading && allItems.length === 0 ? (
        <Center><CircularProgress size={32} /></Center>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <Th
                    key={c.key}
                    onClick={() => handleSort(c.field)}
                    style={c.key === "integrarIFS" ? { textAlign: "center", cursor: "pointer" } : { cursor: "pointer" }}
                  >
                    {c.label} {sortArrow(c.field)}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 && (
                <tr><Td colSpan={3} style={{ textAlign: "center", color: "#888" }}>Nenhum grupo encontrado</Td></tr>
              )}
              {pageItems.map(item => (
                <tr key={item.code}>
                  <Td>{item.code}</Td>
                  <Td>{item.name}</Td>
                  <Td style={{ textAlign: "center" }}>
                    {updating[item.code]
                      ? <CircularProgress size={18} />
                      : (
                        <Switch
                          size="small"
                          checked={item.integrarIFS === "Y"}
                          onChange={() => toggleIntegra(item.code, item.integrarIFS)}
                          color="primary"
                        />
                      )
                    }
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination>
            <PageSizeContainer>
              <PageSizeLabel>Registros por página:</PageSizeLabel>
              <PageSizeSelect value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </PageSizeSelect>
            </PageSizeContainer>
            <PagControls>
              <PagBtn type="button" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={safePage === 0 || loading}>
                ‹ Anterior
              </PagBtn>
              <PagInfo>Página {safePage + 1} de {totalPages}</PagInfo>
              <PagBtn type="button" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={safePage >= totalPages - 1 || loading}>
                Próxima ›
              </PagBtn>
            </PagControls>
          </Pagination>
        </>
      )}
    </Wrapper>
  );
}

export default TabGruposItens;

const Wrapper = styled.div`
  padding: 8px 0;
  font-family: 'Mulish', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Count = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #94a3b8;
`;

const SearchRow = styled.div`
  margin-bottom: 14px;
  display: flex;
  gap: 8px;
`;

const IntegrarSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Mulish', sans-serif;
  outline: none;
  white-space: nowrap;
  flex-shrink: 0;
  &:focus { border-color: #ff6b35; }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-family: 'Mulish', sans-serif;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff6b35; }
`;

const RefreshBtn = styled.button`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { background: #e2e8f0; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
  user-select: none;
  &:hover { background: #f1f5f9; }
`;

const SortIcon = styled.span`
  margin-left: 4px;
  color: ${p => p.active ? "#ff6b35" : "#cbd5e1"};
  font-size: 10px;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const PageSizeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageSizeLabel = styled.span`
  font-size: 13px;
  color: #64748b;
  font-family: 'Mulish', sans-serif;
`;

const PageSizeSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Mulish', sans-serif;
  background: white;
  cursor: pointer;
  outline: none;
  &:focus { border-color: #ff6b35; }
`;

const PagControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PagBtn = styled.button`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  font-family: 'Mulish', sans-serif;
  &:hover:not(:disabled) { background: #e2e8f0; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PagInfo = styled.span`
  font-size: 13px;
  color: #64748b;
  font-family: 'Mulish', sans-serif;
`;

const NoToken = styled.div`
  padding: 32px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;
